import React, { useEffect, useMemo, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import ReactApexChart from "react-apexcharts";
import { plansService } from "../services/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const fmtTry = (n) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const PlanStatsPage = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await plansService.getPlanStats();
        if (res.success) {
          setRows(res.data || []);
          setSummary(res.summary || null);
        } else {
          toast.error(res.message || res.error || "İstatistikler alınamadı");
        }
      } catch {
        toast.error("İstatistikler yüklenirken hata oluştu");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const labels = useMemo(
    () => rows.map((r) => `${r.name} (#${r.id})`),
    [rows]
  );

  const orgSeries = useMemo(
    () => rows.map((r) => Number(r.active_organization_count) || 0),
    [rows]
  );

  const revenueSeries = useMemo(
    () => rows.map((r) => Math.round(Number(r.total_paid_revenue) || 0)),
    [rows]
  );

  const orgChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    dataLabels: { enabled: true },
    xaxis: { categories: labels, labels: { style: { fontSize: "11px" } } },
    tooltip: { y: { formatter: (v) => `${v} organizasyon` } },
    colors: ["#3b82f6"],
  };

  const revenueChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    dataLabels: {
      enabled: true,
      formatter: (v) => fmtTry(v),
    },
    xaxis: { categories: labels, labels: { style: { fontSize: "11px" } } },
    tooltip: { y: { formatter: (v) => fmtTry(v) } },
    colors: ["#10b981"],
  };

  return (
    <MasterLayout>
      <ToastContainer />
      <Breadcrumb title="Plan İstatistikleri" />
      <div className="dashboard-main active">
        <div className="container-fluid">
          {loading ? (
            <div className="card">
              <div className="card-body text-muted">Yükleniyor…</div>
            </div>
          ) : (
            <>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <div className="text-muted small">
                        Platform geneli: aktif/trial aboneliği olan benzersiz
                        organizasyon
                      </div>
                      <div className="h4 mb-0">
                        {summary?.organizations ?? "—"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <div className="text-muted small">
                        Toplam aktif abonelik kaydı
                      </div>
                      <div className="h4 mb-0">
                        {summary?.subscriptions ?? "—"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card h-100">
                    <div className="card-body">
                      <div className="text-muted small">
                        Ödenmiş faturalardan toplam gelir (TRY)
                      </div>
                      <div className="h4 mb-0">
                        {summary != null ? fmtTry(summary.revenue) : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-lg-6">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">
                        Plana göre aktif organizasyon sayısı
                      </h6>
                      <p className="small text-muted mb-2">
                        Aboneliği <code>active</code> veya <code>trial</code> ve{" "}
                        <code>ends_at &gt; NOW()</code> olan kayıtlar.
                      </p>
                      {rows.length ? (
                        <ReactApexChart
                          options={orgChartOptions}
                          series={[{ name: "Organizasyon", data: orgSeries }]}
                          type="bar"
                          height={Math.max(320, rows.length * 36)}
                        />
                      ) : (
                        <div className="text-muted">Veri yok</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">
                        Plana göre ödenmiş fatura geliri
                      </h6>
                      <p className="small text-muted mb-2">
                        <code>saas_invoices</code> tablosunda{" "}
                        <code>status = paid</code> toplamı.
                      </p>
                      {rows.length ? (
                        <ReactApexChart
                          options={revenueChartOptions}
                          series={[{ name: "Gelir", data: revenueSeries }]}
                          type="bar"
                          height={Math.max(320, rows.length * 36)}
                        />
                      ) : (
                        <div className="text-muted">Veri yok</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mt-3">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Plan</th>
                          <th>Kod</th>
                          <th className="text-end">Aktif org</th>
                          <th className="text-end">Aktif abonelik</th>
                          <th className="text-end">Ödenen fatura</th>
                          <th className="text-end">Gelir (TRY)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id}>
                            <td>
                              {r.name}{" "}
                              <span className="text-muted">#{r.id}</span>
                            </td>
                            <td>
                              <code>{r.code}</code>
                            </td>
                            <td className="text-end">
                              {r.active_organization_count}
                            </td>
                            <td className="text-end">
                              {r.active_subscription_count}
                            </td>
                            <td className="text-end">
                              {r.paid_invoice_count}
                            </td>
                            <td className="text-end">
                              {fmtTry(r.total_paid_revenue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default PlanStatsPage;
